import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, FileText, Package, ShoppingCart, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  type Notification,
} from '@/api/queries/notifications';

const CATEGORY_ICONS: Record<string, typeof Bell> = {
  invoice: FileText,
  stock: Package,
  pos: ShoppingCart,
  admin: Shield,
};

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
}

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
}

export function NotificationPanel({ open, onClose }: NotificationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { data, fetchNextPage, hasNextPage } = useNotificationsPaginated();
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);

  if (!open) return null;

  const notifications = data?.notifications ?? [];

  function handleItemClick(notification: Notification) {
    if (!notification.readAt) {
      markAsRead.mutate(notification.id);
    }
    if (notification.data?.route && typeof notification.data.route === 'string' && notification.data.route.startsWith('/')) {
      navigate(notification.data.route);
      onClose();
    }
  }

  return (
    <div
      ref={panelRef}
      role="region"
      aria-label="Notifications"
      className="absolute top-full mt-2 left-4 right-4 sm:left-auto sm:right-0 sm:w-96 max-h-[480px] bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h3 className="font-semibold text-slate-900">Notifications</h3>
        <button
          onClick={() => markAllAsRead.mutate()}
          className="text-sm text-brand-lagune-700 hover:text-brand-lagune-800 font-medium"
        >
          Tout marquer comme lu
        </button>
      </div>

      {/* List */}
      <div className="overflow-y-auto max-h-[380px]">
        {notifications.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-sm">
            Aucune notification
          </div>
        ) : (
          notifications.map((n) => {
            const Icon = CATEGORY_ICONS[n.category] ?? Bell;
            return (
              <button
                key={n.id}
                onClick={() => handleItemClick(n)}
                className={cn(
                  'w-full text-left px-4 py-3 hover:bg-slate-50 flex gap-3 border-b border-slate-50 transition-colors',
                  !n.readAt && 'border-l-2 border-l-brand-lagune-600 bg-brand-lagune-50/30'
                )}
              >
                <div className="flex-shrink-0 mt-0.5">
                  <Icon className="w-4 h-4 text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm', !n.readAt ? 'font-semibold text-slate-900' : 'text-slate-700')}>
                    {n.title}
                  </p>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{n.message}</p>
                  <p className="text-xs text-slate-400 mt-1">{relativeTime(n.createdAt)}</p>
                </div>
                {!n.readAt && (
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-2 h-2 bg-brand-lagune-600 rounded-full" />
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Footer — load more */}
      {notifications.length > 0 && (
        <div className="px-4 py-2 border-t border-slate-100 text-center">
          <button
            onClick={() => fetchNextPage?.()}
            className="text-sm text-slate-500 hover:text-slate-700"
            disabled={!hasNextPage}
          >
            {hasNextPage ? 'Voir plus' : 'Fin des notifications'}
          </button>
        </div>
      )}
    </div>
  );
}

function useNotificationsPaginated() {
  const [page, setPage] = useState(1);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const { data } = useNotifications(page, 20);

  useEffect(() => {
    if (data?.notifications) {
      setAllNotifications((prev) => {
        if (page === 1) return data.notifications;
        const ids = new Set(prev.map((n) => n.id));
        const newItems = data.notifications.filter((n) => !ids.has(n.id));
        return [...prev, ...newItems];
      });
    }
  }, [data, page]);

  return {
    data: { notifications: allNotifications },
    fetchNextPage: data?.pagination?.hasMore ? () => setPage((p) => p + 1) : undefined,
    hasNextPage: data?.pagination?.hasMore ?? false,
  };
}
